<script setup lang="ts">
import Container from "~/components/layout/Container.vue";
import moment from "moment/moment";
const route = useRoute()
useHead({
    title: "Metal Band Production Services | Mixing, Mastering, CDs, Cassettes, Merch | Neuronoir",
    meta: [
        { name: "description", content: "Professional mixing & mastering, CD & cassette production, merchandise printing, and PR services for metal, post-metal, and black metal bands across Europe. Affordable packages for independent artists." },
        { name: 'keywords', content: 'mixing and mastering services, metal band production, CD production, cassette production, band merchandise printing, EPK services, PR for metal bands, independent music production, affordable music production, Romania, Europe' },
        { property: 'og:type', content: 'website' },
        { property: 'og:title', content: 'Metal Band Production Services | Mixing, Mastering, CDs, Cassettes, Merch | Neuronoir' },
        { property: 'og:description', content: 'Professional mixing & mastering, CD & cassette production, merchandise printing, and PR services for metal, post-metal, and black metal bands across Europe. Affordable packages for independent artists.' },
        { property: 'og:url', content: 'https://www.neuronoir.art/services' },
        { property: 'canonical', content: 'https://neuronoir.art' + route.path },
    ],
    script: [
        {
            type: 'application/ld+json',
            children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Service",
                "name": "Metal Band Production Services",
                "provider": {
                    "@type": "Organization",
                    "name": "Neuronoir",
                    "url": "https://neuronoir.art"
                },
                "serviceType": ["Music Production", "Mixing and Mastering", "Physical Media Production", "Merchandise Production", "PR Services"],
                "description": "Professional mixing & mastering, CD & cassette production, merchandise printing, and PR services for metal, post-metal, and black metal bands across Europe.",
                "offers": {
                    "@type": "Offer",
                    "priceSpecification": {
                        "@type": "PriceSpecification",
                        "priceCurrency": "EUR",
                        "minPrice": 75,
                        "maxPrice": 4500
                    }
                }
            })
        }
    ]
})

// Package builder state
const selectedServices = ref<string[]>([]);
const totalPrice = ref(0);
const discountPercentage = ref(0);
const finalPrice = ref(0);

// Service options
const cdOptions = [
  { id: 'cd-100', name: '100 CDs', price: 500 },
  { id: 'cd-250', name: '250 CDs', price: 1125 },
  { id: 'cd-500', name: '500 CDs', price: 2000 }
];

const cassetteOptions = [
  { id: 'cassette-25', name: '25 Cassettes', price: 100 },
  { id: 'cassette-50', name: '50 Cassettes', price: 175 },
  { id: 'cassette-100', name: '100 Cassettes', price: 300 }
];

const tshirtOptions = [
  { id: 'tshirt-100', name: '100 T-Shirts (1 color)', price: 900 },
  { id: 'tshirt-250', name: '250 T-Shirts (1-2 colors)', price: 2250 },
  { id: 'tshirt-500', name: '500 T-Shirts (1-3 colors)', price: 4500 }
];

const addonOptions = [
  { id: 'marketplace', name: 'Custom Marketplace Integration', price: 575 },
  { id: 'epk', name: 'Electronic Press Kit (EPK)', price: 200 },
  { id: 'mockups', name: 'Social Media Assets & Mockups', price: 200 },
  { id: 'mixing', name: 'Mixing & Mastering Services', price: 500 },
  { id: 'pr', name: 'PR Services', price: 200 },
  { id: 'stickers', name: '100 Stickers', price: 75 }
];

// All options combined for easy lookup
const allOptions = [...cdOptions, ...cassetteOptions, ...tshirtOptions, ...addonOptions];

// Toggle service selection
function toggleService(serviceId: string) {
  const index = selectedServices.value.indexOf(serviceId);
  if (index === -1) {
    selectedServices.value.push(serviceId);
  } else {
    selectedServices.value.splice(index, 1);
  }
  calculateTotal();
}

// Calculate total price with discount
function calculateTotal() {
  // Base price calculation
  const basePrice = selectedServices.value.reduce((total, serviceId) => {
    const service = allOptions.find(option => option.id === serviceId);
    return total + (service ? service.price : 0);
  }, 0);

  totalPrice.value = basePrice;

  // Calculate discount (2% per additional item, starting from the second item)
  const additionalItems = Math.max(0, selectedServices.value.length - 1);
  discountPercentage.value = Math.min(20, additionalItems * 2); // Cap at 20% discount

  // Calculate final price with discount
  finalPrice.value = Math.round(basePrice * (1 - discountPercentage.value / 100));
}

// Check if a service is selected
function isSelected(serviceId: string) {
  return selectedServices.value.includes(serviceId);
}

const epks = [
	{
		name: "Falling Leaves",
		description: "Web, PDF, JPG and embedded email template to promote their latest album release.",
		imageUrl: "https://eu-central-1-shared-euc1-02.graphassets.com/AKt48hRDSmuSWQpsU6rJgz/cmffnhulv8l7w07ukvv32dczy",
		link: "https://eu-central-1-shared-euc1-02.graphassets.com/AKt48hRDSmuSWQpsU6rJgz/cmfvcj8zsqojg07w84wp9mvio"
	},
	{
		name: "Paenil Era",
		description: "Web EPK showcasing the band's activity and achievements.",
		imageUrl: "https://eu-central-1-shared-euc1-02.graphassets.com/AKt48hRDSmuSWQpsU6rJgz/cmghpq9z43ls707vz58ivucdi",
		link: "https://paenil-era.neuronoir.art"
	},
	{
		name: "Transmuter",
		description: "Web EPK to open up doors for concerts, festivals and other potential partnerships.",
		imageUrl: "https://eu-central-1-shared-euc1-02.graphassets.com/AKt48hRDSmuSWQpsU6rJgz/cm8h92m8r16mh07w3zdjp9360",
		link: "https://transmuter.neuronoir.art"
	}
]

const mixes = [
	{
		name: "Paenil Era - Idle Cage",
		description: "Post-Black Metal",
		imageUrl: "https://eu-central-1-shared-euc1-02.graphassets.com/AKt48hRDSmuSWQpsU6rJgz/cmght73tlbm3e07vz7qx6k11g",
		link: "https://paenil-era.bandcamp.com/album/idle-cage"
	},
	{
		name: "Soulshedder - Hereditas Naturae",
		description: "Post-Black Metal",
		imageUrl: "https://eu-central-1-shared-euc1-02.graphassets.com/AKt48hRDSmuSWQpsU6rJgz/cmghtck4zbxc507vzl37z1alh",
		link: "https://www.youtube.com/watch?v=kZ4Bv9FkbXE"
	},
	{
		name: "Vaarcloc - Contururi",
		description: "Instrumental Post-Rock/Post-Metal",
		imageUrl: "https://eu-central-1-shared-euc1-02.graphassets.com/AKt48hRDSmuSWQpsU6rJgz/cmght73thbm3907vzcqfwfyax",
		link: "https://www.youtube.com/watch?v=9MpTWCmxru4"
	},
	{
		name: "Hilde - Sous les pavés, la crasse",
		description: "Atmospheric Black Metal",
		imageUrl: "https://eu-central-1-shared-euc1-02.graphassets.com/AKt48hRDSmuSWQpsU6rJgz/cmght73tibgaj07w3x15d6tn7",
		link: "https://hildefr.bandcamp.com/track/sous-les-pav-s-la-crasse"
	},
	{
		name: "Atlantic - Black Sands",
		description: "Atmospheric Black Metal",
		imageUrl: "https://eu-central-1-shared-euc1-02.graphassets.com/AKt48hRDSmuSWQpsU6rJgz/cmght73tabm3007vzirxdln9t",
		link: "https://atlantic-music.bandcamp.com/album/black-sands"
	},
	{
		name: "Hilde - Misere",
		description: "Atmospheric Black Metal",
		imageUrl: "https://eu-central-1-shared-euc1-02.graphassets.com/AKt48hRDSmuSWQpsU6rJgz/cmght73tvbgb207w3rgs069je",
		link: "https://hildefr.bandcamp.com/album/mis-re"
	},
	{
		name: "Ciel - Silent Revolutions Are The Most Effective Ones",
		description: "Post-Rock/Post-Metal",
		imageUrl: "https://eu-central-1-shared-euc1-02.graphassets.com/AKt48hRDSmuSWQpsU6rJgz/cmght73tmbgan07w3xjiajc9t",
		link: "https://www.youtube.com/watch?v=mNqUGw5pj8U"
	},
	{
		name: "From The Depths Of Mexico - Aether",
		description: "Experimental Post-Rock",
		imageUrl: "https://eu-central-1-shared-euc1-02.graphassets.com/AKt48hRDSmuSWQpsU6rJgz/cmght73tubgb007w3cjzwt839",
		link: "https://depthsofmexico.bandcamp.com/album/aether"
	},
]
</script>

<template>
    <div>
        <Container class="bg-neurodarkgreyvar text-white py-10">
            <div class="py-16 flex flex-col items-center text-center">
                <div class="w-2/5 max-md:hidden flex items-center justify-center mb-8">
                    <img src="https://images.unsplash.com/photo-1663433567177-9f94be0bff4c?q=80&w=2670&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"/>
                </div>
                <h1 class="text-5xl font-bold mb-6 max-w-3xl">Metal Band Production Services: From Studio to Merch</h1>
                <p class="text-xl opacity-80 mb-10 max-w-2xl">Professional mixing & mastering, CD & cassette production, merchandise printing, and PR services for metal, post-metal, and black metal bands across Europe. Affordable packages for independent artists.</p>

                <div class="flex flex-col md:flex-row gap-4 mb-16">
                    <nuxt-link to="/about" class="inline-block bg-neuroprimary text-white text-center rounded-md px-8 py-3 font-medium hover:bg-opacity-90 transition-all shadow-lg">
                        Contact us
                    </nuxt-link>
                    <a href="#pricing" class="inline-block bg-white text-neurodarkgrey text-center rounded-md px-8 py-3 font-medium hover:bg-opacity-90 transition-all shadow-lg">
                        View Pricing
                    </a>
                </div>

                <div class="grid grid-cols-4 gap-6 w-full max-w-5xl max-md:grid-cols-2 max-sm:grid-cols-1 py-24">
                    <div class="bg-neurodarkgrey p-6 rounded-lg shadow-lg flex flex-col items-center text-center">
                        <div class="w-12 h-12 bg-neuroprimary bg-opacity-20 rounded-full flex items-center justify-center mb-4">
                          <svg viewBox="0 0 24 24" class="w-6 h-6 text-neuroprimary" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M4 10C4 6.22876 4 4.34315 5.17157 3.17157C6.34315 2 8.22876 2 12 2C15.7712 2 17.6569 2 18.8284 3.17157C20 4.34315 20 6.22876 20 10V14C20 17.7712 20 19.6569 18.8284 20.8284C17.6569 22 15.7712 22 12 22C8.22876 22 6.34315 22 5.17157 20.8284C4 19.6569 4 17.7712 4 14V10Z" stroke="#1C274C" stroke-width="1.5"></path> <path d="M14 7.5C14 8.60457 13.1046 9.5 12 9.5C10.8954 9.5 10 8.60457 10 7.5C10 6.39543 10.8954 5.5 12 5.5C13.1046 5.5 14 6.39543 14 7.5Z" stroke="#1C274C" stroke-width="1.5"></path> <path d="M15 15.5C15 17.1569 13.6569 18.5 12 18.5C10.3431 18.5 9 17.1569 9 15.5C9 13.8431 10.3431 12.5 12 12.5C13.6569 12.5 15 13.8431 15 15.5Z" stroke="#1C274C" stroke-width="1.5"></path> </g></svg>
                        </div>
                        <h3 class="font-bold text-lg mb-2">Mixing & Mastering</h3>
                        <p class="opacity-70 text-sm">Professional mixing & mastering services for metal, post-metal, and black metal bands. Studio-quality sound that captures your unique atmosphere.</p>
                    </div>
                    <div class="bg-neurodarkgrey p-6 rounded-lg shadow-lg flex flex-col items-center text-center">
                        <div class="w-12 h-12 bg-neuroprimary bg-opacity-20 rounded-full flex items-center justify-center mb-4">
                          <svg class="w-6 h-6 text-neuroprimary" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"><path d="M12,2A10,10,0,1,0,22,12,10,10,0,0,0,12,2Zm0,13a3,3,0,1,1,3-3A3,3,0,0,1,12,15Z"></path></g></svg>
                        </div>
                        <h3 class="font-bold text-lg mb-2">Physical Media Production</h3>
                        <p class="opacity-70 text-sm">High-quality CDs and cassettes with professional packaging. Small to large runs available for bands of all sizes. European manufacturing partners.</p>
                    </div>
                    <div class="bg-neurodarkgrey p-6 rounded-lg shadow-lg flex flex-col items-center text-center">
                        <div class="w-12 h-12 bg-neuroprimary bg-opacity-20 rounded-full flex items-center justify-center mb-4">
                            <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-neuroprimary" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <path d="M20.38 3.46 16 2a4 4 0 0 1-8 0L3.62 3.46a2 2 0 0 0-1.34 2.23l.58 3.47a1 1 0 0 0 .99.84H6v10c0 1.1.9 2 2 2h8a2 2 0 0 0 2-2V10h2.15a1 1 0 0 0 .99-.84l.58-3.47a2 2 0 0 0-1.34-2.23z"></path>
                            </svg>
                        </div>
                        <h3 class="font-bold text-lg mb-2">Band Merchandise</h3>
                        <p class="opacity-70 text-sm">Custom screen-printed t-shirts and other band merchandise. High-quality prints with 1-3 colors. Affordable options for metal, post-metal, and black metal bands.</p>
                    </div>
                    <div class="bg-neurodarkgrey p-6 rounded-lg shadow-lg flex flex-col items-center text-center">
                        <div class="w-12 h-12 bg-neuroprimary bg-opacity-20 rounded-full flex items-center justify-center mb-4">
                            <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-neuroprimary" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <path d="M18 3a3 3 0 0 0-3 3v12a3 3 0 0 0 3 3 3 3 0 0 0 3-3 3 3 0 0 0-3-3H6a3 3 0 0 0-3 3 3 3 0 0 0 3 3 3 3 0 0 0 3-3V6a3 3 0 0 0-3-3 3 3 0 0 0-3 3 3 3 0 0 0 3 3h12a3 3 0 0 0 3-3 3 3 0 0 0-3-3z"></path>
                            </svg>
                        </div>
                        <h3 class="font-bold text-lg mb-2">PR & Promotion Services</h3>
                        <p class="opacity-70 text-sm">Professional PR services, EPK creation, and social media assets. Get your music in front of the right audience with targeted promotion for metal and dark music genres.</p>
                    </div>
                </div>
            </div>
        </Container>
        <Container class="bg-neurodarkgrey text-white py-20">
            <div class="max-w-6xl mx-auto py-24">
                <div class="text-center mb-16">
                    <div class="inline-block px-4 py-1 bg-neuroprimary bg-opacity-20 rounded-full text-neuroprimary text-sm font-medium mb-4">WHY CHOOSE NEURONOIR</div>
                    <h2 class="text-3xl font-bold mb-6">Complete Metal Band Production Services for Artists of All Sizes</h2>
                    <p class="text-lg opacity-70 max-w-2xl mx-auto">From emerging underground metal acts to established post-rock artists, we provide tailored production services to meet your specific needs and budget. Based in Romania, serving bands across Europe.</p>
                </div>

                <div class="grid grid-cols-2 gap-8 mb-16 max-md:grid-cols-1">
                    <div class="bg-neurodarkgreyvar rounded-xl overflow-hidden shadow-lg">
                        <div class="p-8">
                            <div class="flex items-center mb-6">
                                <div class="w-12 h-12 bg-neuroprimary rounded-full flex items-center justify-center mr-4">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                        <path d="M12 8a2 2 0 0 1-2-2 2 2 0 0 1 2-2 2 2 0 0 1 2 2 2 2 0 0 1-2 2z"></path>
                                        <path d="M12 2v2"></path>
                                        <path d="M12 6v14"></path>
                                        <path d="M8 18a4 4 0 0 1 8 0"></path>
                                        <path d="M2 22h20"></path>
                                    </svg>
                                </div>
                                <h3 class="text-xl font-bold">For Emerging Artists</h3>
                            </div>
                            <ul class="space-y-3">
                                <li class="flex items-start">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 text-neuroprimary mr-2 mt-0.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                        <polyline points="20 6 9 17 4 12"></polyline>
                                    </svg>
                                    <span>Affordable small-batch production (25-100 units)</span>
                                </li>
                                <li class="flex items-start">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 text-neuroprimary mr-2 mt-0.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                        <polyline points="20 6 9 17 4 12"></polyline>
                                    </svg>
                                    <span>Professional mixing and mastering services</span>
                                </li>
                                <li class="flex items-start">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 text-neuroprimary mr-2 mt-0.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                        <polyline points="20 6 9 17 4 12"></polyline>
                                    </svg>
                                    <span>Basic promotional materials and social media assets</span>
                                </li>
                                <li class="flex items-start">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 text-neuroprimary mr-2 mt-0.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                        <polyline points="20 6 9 17 4 12"></polyline>
                                    </svg>
                                    <span>Guidance on digital distribution and release strategy</span>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div class="bg-neurodarkgreyvar rounded-xl overflow-hidden shadow-lg">
                        <div class="p-8">
                            <div class="flex items-center mb-6">
                                <div class="w-12 h-12 bg-neuroprimary rounded-full flex items-center justify-center mr-4">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                        <path d="m12 8 6-3-6-3v10"></path>
                                        <path d="m8 11.99-5.5 3.14a1 1 0 0 0 0 1.74l8.5 4.86a2 2 0 0 0 2 0l8.5-4.86a1 1 0 0 0 0-1.74L16 12"></path>
                                        <path d="m6.49 12.85 11.02 6.3"></path>
                                        <path d="M17.51 12.85 6.5 19.15"></path>
                                    </svg>
                                </div>
                                <h3 class="text-xl font-bold">For Established Bands</h3>
                            </div>
                            <ul class="space-y-3">
                                <li class="flex items-start">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 text-neuroprimary mr-2 mt-0.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                        <polyline points="20 6 9 17 4 12"></polyline>
                                    </svg>
                                    <span>Larger production runs (250-500+ units)</span>
                                </li>
                                <li class="flex items-start">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 text-neuroprimary mr-2 mt-0.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                        <polyline points="20 6 9 17 4 12"></polyline>
                                    </svg>
                                    <span>Multi-format releases (CD, cassette, and merchandise bundles)</span>
                                </li>
                                <li class="flex items-start">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 text-neuroprimary mr-2 mt-0.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                        <polyline points="20 6 9 17 4 12"></polyline>
                                    </svg>
                                    <span>Comprehensive PR campaigns and electronic press kits</span>
                                </li>
                                <li class="flex items-start">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 text-neuroprimary mr-2 mt-0.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                        <polyline points="20 6 9 17 4 12"></polyline>
                                    </svg>
                                    <span>Custom marketplace integration and bespoke ecommerce solutions</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>

                <div class="grid grid-cols-3 gap-6 max-md:grid-cols-2 max-sm:grid-cols-1">
                    <div class="bg-neurodarkgreyvar p-6 rounded-lg border border-gray-700">
                        <div class="flex items-center mb-4">
                            <div class="w-10 h-10 bg-neuroprimary bg-opacity-20 rounded-full flex items-center justify-center mr-3">
                                <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 text-neuroprimary" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                    <path d="M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z"></path>
                                    <path d="M6 12h12"></path>
                                    <path d="M12 6v12"></path>
                                </svg>
                            </div>
                            <h4 class="font-bold">10+ Years Experience</h4>
                        </div>
                        <p class="opacity-70 text-sm">From musicians, for musicians. We've finalized numerous releases for ourselves and clients, and now it's your turn.</p>
                    </div>
                    <div class="bg-neurodarkgreyvar p-6 rounded-lg border border-gray-700">
                        <div class="flex items-center mb-4">
                            <div class="w-10 h-10 bg-neuroprimary bg-opacity-20 rounded-full flex items-center justify-center mr-3">
                                <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 text-neuroprimary" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                                    <polyline points="3.29 7 12 12 20.71 7"></polyline>
                                    <line x1="12" y1="22" x2="12" y2="12"></line>
                                </svg>
                            </div>
                            <h4 class="font-bold">Premium Quality</h4>
                        </div>
                        <p class="opacity-70 text-sm">We work with the best European manufacturing partners to ensure top-quality physical products for your releases.</p>
                    </div>
                    <div class="bg-neurodarkgreyvar p-6 rounded-lg border border-gray-700">
                        <div class="flex items-center mb-4">
                            <div class="w-10 h-10 bg-neuroprimary bg-opacity-20 rounded-full flex items-center justify-center mr-3">
                                <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 text-neuroprimary" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                    <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z"></path>
                                    <path d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8z"></path>
                                    <path d="M12 12v8"></path>
                                </svg>
                            </div>
                            <h4 class="font-bold">Custom Solutions</h4>
                        </div>
                        <p class="opacity-70 text-sm">Every band is unique. We offer flexible packages that can be tailored to your specific needs and vision.</p>
                    </div>
                </div>
            </div>
        </Container>
        <Container class="bg-neurodarkgreyvar text-white py-20">
          <h2 class="text-xl font-bold pt-16">Portfolio</h2>
          <p class="opacity-70 max-w-2xl">Examples of our work</p>

	        <div class="py-10">
		        <div>EPKs (Electronic Press Kits)</div>
		        <table class="table-auto w-full">
			        <tbody>
			        <tr v-for="epk, i in epks" :key="i">
				        <td class="flex my-3">
					        <nuxt-img :alt="epk.name" :src="epk.imageUrl" class="w-32 max-lg:hidden" />
					        <div class="my-auto lg:pl-3">
						        <div class="uppercase font-bold text-white">{{epk.name}}</div>
						        <div class="text-white font-light opacity-50">{{epk.description}}</div>
					        </div>
				        </td>
				        <td>
					        <a target="_blank" :href="epk.link" class="block text-center bg-neuroprimary font-light px-6 py-2 text-white rounded">
						        Learn more
					        </a>
				        </td>
			        </tr>
			        </tbody>
		        </table>
	        </div>

	        <div class="py-10">
		        <div>Mixing & Mastering</div>
		        <table class="table-auto w-full">
			        <tbody>
			        <tr v-for="mix, i in mixes" :key="i">
				        <td class="flex my-3">
					        <nuxt-img :alt="mix.name" :src="mix.imageUrl" class="w-32 max-lg:hidden" />
					        <div class="my-auto lg:pl-3">
						        <div class="uppercase font-bold text-white">{{mix.name}}</div>
						        <div class="text-white font-light opacity-50">{{mix.description}}</div>
					        </div>
				        </td>
				        <td>
					        <a target="_blank" :href="mix.link" class="block text-center bg-neuroprimary font-light px-6 py-2 text-white rounded">
						        Learn more
					        </a>
				        </td>
			        </tr>
			        </tbody>
		        </table>
	        </div>
        </Container>
        <Container class="bg-neurodarkgrey text-white py-20">
            <div class="text-3xl font-semibold pt-10 pb-5 text-center my-auto" id="pricing">Build your own production package</div>
            <div class="w-3/4 max-sm:w-full mx-auto pb-10">
                <p class="text-center text-gray-300 mb-10 max-w-2xl mx-auto">Select the metal band production services you need and get a <span class="text-neuroprimary font-medium">2% discount</span> for each additional service you add. Affordable options for independent artists.</p>

                <!-- CDs Section -->
                <div class="mb-12">
                    <h3 class="text-xl mb-4 border-b border-gray-700 pb-2 flex items-center"><span class="w-8 h-8 bg-neuroprimary rounded-full flex items-center justify-center mr-3 text-sm">CD</span>CDs</h3>
                    <div class="grid grid-cols-3 max-sm:grid-cols-1 gap-4">
                        <div v-for="option in cdOptions" :key="option.id"
                             :class="['border rounded-lg px-4 py-4 cursor-pointer transition-all hover:shadow-md',
                                     isSelected(option.id) ? 'border-neuroprimary bg-neurodarkgreyvar shadow-[0_0_10px_rgba(255,87,51,0.15)]' : 'border-gray-600 hover:border-gray-400']"
                             @click="toggleService(option.id)">
                            <div class="flex justify-between items-center">
                                <div>
                                    <div class="font-medium">{{ option.name }}</div>
                                    <div class="text-sm opacity-70">€{{ option.price }}</div>
                                </div>
                                <div v-if="isSelected(option.id)" class="text-neuroprimary bg-opacity-20 bg-neuroprimary w-6 h-6 rounded-full flex items-center justify-center">✓</div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Cassettes Section -->
                <div class="mb-12">
                    <h3 class="text-xl mb-4 border-b border-gray-700 pb-2 flex items-center"><span class="w-8 h-8 bg-neuroprimary rounded-full flex items-center justify-center mr-3 text-sm">C</span>Cassettes</h3>
                    <div class="grid grid-cols-3 max-sm:grid-cols-1 gap-4">
                        <div v-for="option in cassetteOptions" :key="option.id"
                             :class="['border rounded-lg px-4 py-3 cursor-pointer transition-all',
                                     isSelected(option.id) ? 'border-neuroprimary bg-neurodarkgreyvar' : 'border-gray-600 hover:border-gray-400']"
                             @click="toggleService(option.id)">
                            <div class="flex justify-between items-center">
                                <div>
                                    <div class="font-medium">{{ option.name }}</div>
                                    <div class="text-sm opacity-70">€{{ option.price }}</div>
                                </div>
                                <div v-if="isSelected(option.id)" class="text-neuroprimary">✓</div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- T-Shirts Section -->
                <div class="mb-12">
                    <h3 class="text-xl mb-4 border-b border-gray-700 pb-2 flex items-center"><span class="w-8 h-8 bg-neuroprimary rounded-full flex items-center justify-center mr-3 text-sm">T</span>T-Shirts (screen printed)</h3>
                    <div class="grid grid-cols-3 max-sm:grid-cols-1 gap-4">
                        <div v-for="option in tshirtOptions" :key="option.id"
                             :class="['border rounded-lg px-4 py-3 cursor-pointer transition-all',
                                     isSelected(option.id) ? 'border-neuroprimary bg-neurodarkgreyvar' : 'border-gray-600 hover:border-gray-400']"
                             @click="toggleService(option.id)">
                            <div class="flex justify-between items-center">
                                <div>
                                    <div class="font-medium">{{ option.name }}</div>
                                    <div class="text-sm opacity-70">€{{ option.price }}</div>
                                </div>
                                <div v-if="isSelected(option.id)" class="text-neuroprimary">✓</div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Add-ons Section -->
                <div class="mb-12">
                    <h3 class="text-xl mb-4 border-b border-gray-700 pb-2 flex items-center"><span class="w-8 h-8 bg-neuroprimary rounded-full flex items-center justify-center mr-3 text-sm">+</span>Add-ons</h3>
                    <div class="grid grid-cols-3 max-sm:grid-cols-1 gap-4">
                        <div v-for="option in addonOptions" :key="option.id"
                             :class="['border rounded-lg px-4 py-3 cursor-pointer transition-all',
                                     isSelected(option.id) ? 'border-neuroprimary bg-neurodarkgreyvar' : 'border-gray-600 hover:border-gray-400']"
                             @click="toggleService(option.id)">
                            <div class="flex justify-between items-center">
                                <div>
                                    <div class="font-medium">{{ option.name }}</div>
                                    <div class="text-sm opacity-70">€{{ option.price }}</div>
                                </div>
                                <div v-if="isSelected(option.id)" class="text-neuroprimary">✓</div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Summary Section -->
                <div class="border border-gray-600 rounded-lg p-6 mb-8 bg-neurodarkgreyvar shadow-lg">
                    <h3 class="text-xl mb-4 font-semibold flex items-center">
                        <span class="w-8 h-8 bg-neuroprimary rounded-full flex items-center justify-center mr-3 text-sm">$</span>
                        Your Package Summary
                    </h3>

                    <div v-if="selectedServices.length === 0" class="opacity-70 italic mb-4">
                        No services selected yet. Click on the options above to build your package.
                    </div>

                    <div v-else>
                        <div class="mb-4">
                            <div v-for="serviceId in selectedServices" :key="serviceId" class="flex justify-between py-1">
                                <div>{{ allOptions.find(o => o.id === serviceId)?.name }}</div>
                                <div>€{{ allOptions.find(o => o.id === serviceId)?.price }}</div>
                            </div>
                        </div>

                        <div class="border-t border-gray-700 pt-4 mt-4 mb-2">
                            <div class="flex justify-between">
                                <div>Subtotal:</div>
                                <div>€{{ totalPrice }}</div>
                            </div>
                        </div>

                        <div v-if="discountPercentage > 0" class="flex justify-between text-neuroprimary mb-2">
                            <div>Discount ({{ discountPercentage }}%):</div>
                            <div>-€{{ totalPrice - finalPrice }}</div>
                        </div>

                        <div class="flex justify-between font-bold text-xl mt-4">
                            <div>Total:</div>
                            <div class="text-white">€{{ finalPrice }}</div>
                        </div>
                    </div>
                </div>

                <div class="col-span-2 mt-8">
                    <nuxt-link to="/about" class="w-full inline-block bg-neuroprimary text-white text-center rounded-md px-8 py-3 mr-2 font-medium hover:bg-opacity-90 transition-all shadow-lg hover:shadow-xl">
                        Contact us about your production package
                    </nuxt-link>
                </div>
            </div>
        </Container>
        
        <!-- FAQ Section -->
        <Container class="bg-neurodarkgreyvar text-white py-20">
            <div class="max-w-4xl mx-auto">
                <h2 class="text-3xl font-bold mb-12 text-center">Frequently Asked Questions</h2>
                
                <div class="space-y-6">
                    <div class="bg-neurodarkgrey p-6 rounded-lg">
                        <h3 class="text-xl font-semibold mb-3">What genres do you specialize in?</h3>
                        <p class="opacity-80">We specialize in metal, post-metal, black metal, post-rock, and adjacent dark music genres.</p>
                    </div>
                    
                    <div class="bg-neurodarkgrey p-6 rounded-lg">
                        <h3 class="text-xl font-semibold mb-3">How long does the production process take?</h3>
                        <p class="opacity-80">Production timelines vary based on the services selected. Mixing and mastering typically takes 1-2 weeks per song, while physical media production (CDs, cassettes) requires 8-12 weeks. Merchandise production usually takes 3-6 weeks. We'll provide a detailed timeline when discussing your specific project.</p>
                    </div>
                    
                    <div class="bg-neurodarkgrey p-6 rounded-lg">
                        <h3 class="text-xl font-semibold mb-3">Do you work with bands outside of Romania?</h3>
                        <p class="opacity-80">Yes! While we're based in Romania, we work with bands across Europe and internationally. Our digital services (mixing, mastering, EPK creation) can be delivered worldwide, and we ship physical products throughout the European Union.</p>
                    </div>
                    
                    <div class="bg-neurodarkgrey p-6 rounded-lg">
                        <h3 class="text-xl font-semibold mb-3">What's included in the EPK (Electronic Press Kit) service?</h3>
                        <p class="opacity-80">Our EPK service includes a professionally designed digital press kit. You provide the content, we create web, PDF, and email-ready versions to help you promote your music to venues, festivals, and media outlets.</p>
                    </div>
                    
                    <div class="bg-neurodarkgrey p-6 rounded-lg">
                        <h3 class="text-xl font-semibold mb-3">Can you create custom packages not listed here?</h3>
                        <p class="opacity-80">Absolutely! The package builder is a starting point, but we're happy to create custom solutions based on your specific needs and budget. Contact us to discuss your project requirements, and we'll put together a tailored proposal.</p>
                    </div>
                    
                    <div class="bg-neurodarkgrey p-6 rounded-lg">
                        <h3 class="text-xl font-semibold mb-3">What payment methods do you accept?</h3>
                        <p class="opacity-80">We accept bank transfers, PayPal, and major credit cards.</p>
                    </div>
                </div>
            </div>
        </Container>
    </div>

</template>
